"use client";

import React from "react";
import { MessageCircle, LoaderCircle, ChevronDown } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
function CtaFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-[var(--chart-2)]/10 border border-[var(--chart-2)]/20 rounded-xl text-center flex flex-col items-center justify-center space-y-4 min-h-[300px]">
                <div className="size-16 rounded-lg bg-[var(--chart-2)]/20 flex items-center justify-center mb-2">
                    <MessageCircle className="size-8 text-[var(--chart-2)]" />
                </div>
                <h3 className="text-xl font-default font-bold text-foreground">פנייתך התקבלה!</h3>
                <p className="text-muted-foreground">{successMessage}</p>
            </div>;
  }
  const focusClasses = "focus-visible:ring-[var(--chart-2)]/50 focus-visible:border-[var(--chart-2)]";
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <FormField control={control} name="full_name" rules={{
      required: "שדה זה הוא חובה"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>שם מלא</FormLabel>
                        <FormControl>
                            <Input placeholder="לדוגמה: יוסי כהן" className={focusClasses} {...field} />
                        </FormControl>
                        <FormDescription>אנא הזינו את שמכם המלא</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="phone_number" rules={{
      required: "שדה זה הוא חובה",
      pattern: {
        value: /^[\d\+\-\s()]+$/,
        message: "מספר טלפון לא תקין"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>מספר טלפון</FormLabel>
                        <FormControl>
                            <Input type="tel" placeholder="לדוגמה: 050-1234567" className={`${focusClasses} text-right`} dir="ltr" {...field} />
                        </FormControl>
                        <FormDescription>ניצור איתכם קשר בטלפון – הודעת SMS לא תשלח</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="email_address" rules={{
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "כתובת אימייל לא תקינה"
      }
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>כתובת אימייל <span className="text-muted-foreground font-normal text-xs">(אופציונלי)</span></FormLabel>
                        <FormControl>
                            <Input type="email" placeholder="לדוגמה: you@example.com" className={`${focusClasses} text-right`} dir="ltr" {...field} />
                        </FormControl>
                        <FormDescription>אפשר להוסיף אימייל לקבלת מסמכי מידע</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="service_type" rules={{
      required: "אנא בחרו סוג שירות"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel>סוג השירות המבוקש</FormLabel>
                        <FormControl>
                            <select {...field} className={`flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm ${focusClasses}`}>
                                <option value="" disabled>בחרו סוג שירות</option>
                                {["מטפל סיעודי בבית", "השגחה בבית חולים", "מטפל סיעודי פרטי", "מטפל/ת לילה"].map((opt, i) => <option key={i} value={opt} data-index={i}>
                                        {opt}
                                    </option>)}
                            </select>
                        </FormControl>
                        <FormDescription>הקפידו לבחור את השירות המתאים לצרכיכם</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
                    {submitError}
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold bg-[var(--chart-2)] hover:bg-[var(--chart-2)]/90 text-white shadow-lg transition-all">
                {isSubmitting ? <>
                        <LoaderCircle className="animate-spin mr-2 size-5" />
                        שולח פנייה...
                    </> : 'שלחו פנייה עכשיו'}
            </Button>
        </Form>;
}
export default function CallToAction() {
  const cityLinks = [{
    text: "מטפל סיעודי בתל אביב",
    url: "/NursingCaregiverInTelAvivWithinFiveHours"
  }, {
    text: "מטפל סיעודי בירושלים",
    url: "/NursingCareProviderInJerusalemWithinFiveHours"
  }, {
    text: "מטפל סיעודי בחיפה",
    url: "/NursingCareProviderInHaifaWithinHours"
  }, {
    text: "מטפל סיעודי ברמת גן",
    url: "/NursingCareInRamatGanWithinHours"
  }];
  const generalLinks = [{
    text: "מטפל סיעודי",
    url: "/"
  }, {
    text: "מטפל סיעודי ישראלי",
    url: "/blog"
  }];
  return <section id="cta" className="relative bg-primary py-[100px] overflow-hidden" dir="rtl">
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[var(--ring)] opacity-10 blur-[120px] rounded-lg mix-blend-screen translate-x-1/3 -translate-y-1/4"></div>
                <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-[var(--ring)] opacity-[0.07] blur-[150px] rounded-lg mix-blend-screen -translate-x-1/4 translate-y-1/3"></div>
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
                    
                    <div className="flex flex-col items-center lg:items-start text-center lg:text-right order-1 lg:order-2">
                        <div className="mb-8 relative">
                            <div className="absolute inset-0 bg-[var(--chart-2)] blur-3xl opacity-20 rounded-lg animate-pulse"></div>
                            <div className="relative bg-primary-foreground/10 p-6 rounded-lg border border-primary-foreground/20 backdrop-blur-sm">
                                <MessageCircle className="size-20 md:size-24 text-[var(--chart-2)] animate-pulse" strokeWidth={1.5} />
                            </div>
                        </div>
                        <h2 className="text-primary-foreground font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                            צריך מטפל עכשיו?<br />
                            <span className="text-[var(--chart-2)]">אנחנו בדרך</span>
                        </h2>
                        <p className="text-primary-foreground/80 text-lg md:text-xl max-w-md">
                            השאירו פרטים ונציג מקצועי יחזור אליכם בהקדם האפשרי להתאמת הטיפול המדויק עבורכם.
                        </p>
                        
                        <div className="mt-10 space-y-8 text-primary-foreground/80 text-right max-w-2xl">
                            <p className="text-lg leading-relaxed">
                                בחירת <strong>מטפל סיעודי</strong> היא צעד משמעותי עבור המשפחה. אנו מציעים פתרונות מקיפים הכוללים <strong>מטפל סיעודי בבית</strong> לטווח ארוך או <strong>מטפל סיעודי פרטי</strong> למקרים דחופים, תוך הבטחת מקצועיות וליווי אישי לכל אורך הדרך.
                            </p>

                            <div className="flex flex-wrap gap-x-4 gap-y-2">
                                {cityLinks.map((link, i) => <Link key={i} to={link.url} data-index={i} className="text-[var(--chart-2)] hover:text-[var(--chart-2)]/80 underline underline-offset-4 transition-colors font-medium">
                                        {link.text}
                                    </Link>)}
                                {generalLinks.map((link, i) => <Link key={`gen-${i}`} to={link.url} data-index={i + cityLinks.length} className="text-primary-foreground/60 hover:text-primary-foreground underline underline-offset-4 transition-colors">
                                        {link.text}
                                    </Link>)}
                            </div>
                            
                            <div className="space-y-4">
                                <h3 className="text-2xl font-bold text-[var(--chart-2)]">הפתרון שלנו</h3>
                                <p className="text-lg leading-relaxed">
                                    הפתרון שלנו מבוסס על מהירות והתאמה מדויקת. אנו מספקים <strong>מטפל סיעודי</strong> מוסמך תוך 5 שעות בלבד בכל מקום בארץ. בין אם נדרש <strong>מטפל סיעודי בבית</strong> למגורים מלאים או <strong>מטפל סיעודי פרטי</strong> להשגחה בבית חולים, אנו דואגים לכל הפרטים הבירוקרטיים והמקצועיים.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <h3 className="text-2xl font-bold text-[var(--chart-2)]">יתרונות השירות</h3>
                                <ul className="space-y-4">
                                    <li className="flex items-start gap-3" data-index={0}>
                                        <div className="mt-1.5 size-2 rounded-lg bg-[var(--chart-2)] shrink-0" />
                                        <span>מענה מיידי וזמינות של <strong>מטפל סיעודי</strong> 24/7 לכל צורך רפואי.</span>
                                    </li>
                                    <li className="flex items-start gap-3" data-index={1}>
                                        <div className="mt-1.5 size-2 rounded-lg bg-[var(--chart-2)] shrink-0" />
                                        <span>התאמה אישית של <strong>מטפל סיעודי בבית</strong> תוך התחשבות בשפה, תרבות וצרכים רפואיים.</span>
                                    </li>
                                    <li className="flex items-start gap-3" data-index={2}>
                                        <div className="mt-1.5 size-2 rounded-lg bg-[var(--chart-2)] shrink-0" />
                                        <span>ביטחון מלא עם <strong>מטפל סיעודי פרטי</strong> בעל ניסיון מוכח והמלצות חמות.</span>
                                    </li>
                                </ul>
                            </div>

                            <div className="space-y-4">
                                <h3 className="text-2xl font-bold text-[var(--chart-2)]">מידע נוסף</h3>
                                <p className="text-lg leading-relaxed">
                                    כאשר אתם פונים אלינו למציאת <strong>מטפל סיעודי</strong>, אתם מקבלים מעטפת מלאה. אנו מבצעים סינון קפדני של כל <strong>מטפל סיעודי בבית</strong> כדי להבטיח איכות טיפול גבוהה. בנוסף, שירותי <strong>מטפל סיעודי פרטי</strong> מטעמנו כוללים השגחה בבתי חולים, סיוע בשיקום וליווי אישי המעניק שקט נפשי למשפחה כולה.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="order-2 lg:order-1 flex flex-col gap-8">
                        <Card className="bg-card rounded-lg border-none shadow-2xl shadow-black/50 overflow-hidden">
                            <CardHeader className="bg-muted/30 border-b border-border/50 pb-6">
                                <CardTitle className="text-2xl font-default text-center text-foreground">
                                    קריאה לפעולה – טופס יצירת קשר
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="pt-8">
                                <FormProvider formId="cta_form_caregiverservicepages" sectionName="CallToAction" defaultValues={{
                full_name: "",
                phone_number: "",
                email_address: "",
                service_type: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                phone_number: {
                  required: true,
                  pattern: "phone"
                },
                email_address: {
                  pattern: "email"
                },
                service_type: {
                  required: true
                }
              }} submitText="שלחו פנייה עכשיו" successMessage="תודה רבה! פנייתך התקבלה בהצלחה. נציג מטעמנו ייצור איתך קשר בהקדם האפשרי.">
                                    <CtaFormContent />
                                </FormProvider>
                            </CardContent>
                        </Card>

                        <div className="bg-primary-foreground/5 p-8 rounded-lg border border-primary-foreground/10">
                            <h3 className="text-2xl font-bold text-primary-foreground mb-6">שאלות נפוצות</h3>
                            <div className="space-y-2">
                                {[{
                q: "תוך כמה זמן ניתן לקבל מטפל סיעודי?",
                a: "אנו מתחייבים לספק מטפל סיעודי מקצועי תוך עד 5 שעות בלבד מרגע הפנייה הראשונית."
              }, {
                q: "האם השירות כולל מטפל סיעודי פרטי לבתי חולים?",
                a: "בהחלט, ניתן להזמין מטפל סיעודי פרטי להשגחה צמודה בבתי חולים בכל רחבי הארץ, כולל בסופי שבוע וחגים."
              }, {
                q: "מה היתרון של מטפל סיעודי בבית על פני מוסד?",
                a: "שירותי מטפל סיעודי בבית מאפשרים למטופל להישאר בסביבתו הטבעית והמוכרת, מה שמשפר משמעותית את איכות החיים וההחלמה."
              }, {
                q: "איך מתבצע תהליך בחירת מטפל סיעודי?",
                a: "אנו מבצעים התאמה קפדנית של מטפל סיעודי לפי הצרכים הרפואיים, השפה והעדפות המשפחה כדי להבטיח חיבור אנושי מצוין."
              }, {
                q: "האם ניתן להזמין מטפל סיעודי בבית ללילה בלבד?",
                a: "כן, ניתן לקבל שירות של מטפל סיעודי בבית או מטפל סיעודי פרטי למשמרות לילה, יום או סביב השעון לפי הצורך המשתנה שלכם."
              }].map((item, i) => <details key={i} className="group border-b border-primary-foreground/10 last:border-0" data-index={i}>
                                        <summary className="flex items-center justify-between cursor-pointer py-4 list-none text-primary-foreground hover:text-[var(--chart-2)] transition-colors outline-none">
                                            <span className="font-bold text-lg text-right">{item.q}</span>
                                            <ChevronDown className="size-5 transition-transform group-open:rotate-180 shrink-0 mr-4" />
                                        </summary>
                                        <div className="pb-4 text-primary-foreground/70 leading-relaxed text-right">
                                            {item.a}
                                        </div>
                                    </details>)}
                            </div>
                            
                            <div className="mt-10 flex justify-center">
                                <Button asChild variant="outline" className="h-12 px-8 text-base font-bold border-[var(--chart-2)] text-[var(--chart-2)] hover:bg-[var(--chart-2)] hover:text-white transition-all rounded-lg">
                                    <Link to="/">
                                        לייעוץ חינם עם מומחה סיעוד
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}